import { useCallback, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import type { ProjectConferenceTagWithMetadata } from '@trinity/chat/api/interfaces/projects';
import type { TagCategory } from '@trinity/chat';
import { Modal, Header } from '../Modal';
import type { UseModalProps } from '../Modal';
import Button from '../presentation/Button';
import { ButtonActivityIndicator } from '../presentation/Button.ActivityIndicator';
import { Colors } from '../ColorSelect';
import { TagStateContext } from '../../contexts/project-tags';
import type { CategoryOption } from './TagForm';
import { TagForm } from './TagForm';
import { TagFormContainer } from './TagFormContainer';
import { TagFormContext } from './TagFormContext';

export type CreateTagFormParams = {
  tag: {
    baseTag: {
      color: string;
      name: string;
      id: null;
      tagCategory: TagCategory | null;
    };
    parentTagId: number | null;
    ordinal: number;
  };
};

type Props = UseModalProps & {
  parentTagId: number | null;
  defaultColor?: string;
  allowedCategories?: CategoryOption[];
  onSave: (params: CreateTagFormParams) => Promise<unknown>;
  isPending: boolean;
};

export const CreateTag = ({
  open,
  onClose,
  parentTagId,
  defaultColor = Colors.defaultColor,
  allowedCategories,
  onSave,
  isPending,
}: Props) => {
  return (
    <Modal open={open} onClose={onClose}>
      <Header>Add New Tag</Header>
      <TagFormContainer
        defaultState={{
          parentId: parentTagId,
          title: '',
          color: defaultColor,
          category: null,
          includeSummary: true,
        }}
      >
        <ModalContent
          onClose={onClose}
          parentTagId={parentTagId}
          allowedCategories={allowedCategories}
          onSave={onSave}
          isPending={isPending}
        />
      </TagFormContainer>
    </Modal>
  );
};

type ContentProps = {
  onClose?: () => unknown;
  parentTagId: number | null;
  allowedCategories?: CategoryOption[];
  onSave: (params: CreateTagFormParams) => Promise<unknown>;
  isPending: boolean;
};

const ModalContent = ({ onClose, parentTagId, allowedCategories, onSave, isPending }: ContentProps) => {
  const stateCtx = useContext(TagStateContext);
  const formCtx = useContext(TagFormContext);

  if (!stateCtx) throw new Error('CreateTag must be used within a TagStateContext provider');
  if (!formCtx) throw new Error('CreateTag must be used within a TagFormContext provider');

  const { state } = stateCtx;
  const { data } = formCtx;

  const canSave = useMemo(() => tagFormIsValid(data), [data]);

  const allTags = state.tags.flatMap((t) => [t, ...(t.children ?? [])]);
  const maxOrdinal = allTags.length > 0 ? Math.max(...allTags.map((t) => t.ordinal)) : 0;

  const handleSave = useCallback(() => {
    onSave({
      tag: {
        baseTag: {
          color: data.color,
          name: data.title,
          id: null,
          tagCategory: data.category,
        },
        parentTagId: data.parentId,
        ordinal: maxOrdinal + 1,
      },
    }).then(onClose);
  }, [onSave, onClose, data, maxOrdinal]);

  return (
    <>
      <TagForm disableParent={!!parentTagId} allowedCategories={allowedCategories} />
      <Actions>
        <Button variant="brick" color="destructive" onClick={onClose}>
          Cancel
        </Button>
        <ButtonActivityIndicator
          disabled={!canSave}
          implicitDisable={false}
          loading={isPending}
          variant="brick"
          onClick={handleSave}
        >
          Save
        </ButtonActivityIndicator>
      </Actions>
    </>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const tagFormIsValid = (form: { title: string }) => {
  return form.title?.length > 3 && form.title.length < 64 && form.title.replace(/\s/g, '').length > 3;
};

// ─── Styled ──────────────────────────────────────────────────────────────────

const Actions = styled.div`
  display: flex;
  justify-content: space-evenly;
`;
